import path from 'node:path';

export interface AppConfig {
  port: number;
  host: string;
  clientOrigin: string;
  timeoutMs: number;
  maxRedirects: number;
  maxBodyBytes: number;
  maxConcurrency: number;
  rateLimitWindowMs: number;
  rateLimitMax: number;
  userAgent: string;
  trustProxy: boolean;
  serveStatic: boolean;
  enableHsts: boolean;
  siteUrl: string | null;
  searchLogPath: string | null;
}

export const defaultConfig: AppConfig = {
  port: 3001,
  host: '127.0.0.1',
  clientOrigin: 'http://localhost:5173',
  timeoutMs: 8000,
  maxRedirects: 3,
  maxBodyBytes: 262_144,
  maxConcurrency: 4,
  rateLimitWindowMs: 60_000,
  rateLimitMax: 10,
  userAgent: 'PHP2ShellScanner/1.0 (+non-invasive HTTP assessment)',
  trustProxy: false,
  serveStatic: false,
  enableHsts: false,
  siteUrl: 'https://php2shellscanner.cambridgedigitalinstitute.com',
  searchLogPath: path.resolve(process.cwd(), 'data', 'searches.json'),
};

function clamp(value: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.trunc(value)));
}

export function normalizeConfig(overrides: Partial<AppConfig> = {}): AppConfig {
  const merged = { ...defaultConfig, ...overrides };
  let clientOrigin = defaultConfig.clientOrigin;
  try {
    clientOrigin = new URL(merged.clientOrigin).origin;
  } catch {
    clientOrigin = defaultConfig.clientOrigin;
  }

  const userAgent = merged.userAgent.replace(/[\r\n]/g, '').trim().slice(0, 180);

  return {
    port: clamp(merged.port, 1, 65_535, defaultConfig.port),
    host: merged.host.trim() || defaultConfig.host,
    clientOrigin,
    timeoutMs: clamp(merged.timeoutMs, 1_000, 30_000, defaultConfig.timeoutMs),
    maxRedirects: clamp(merged.maxRedirects, 0, 3, defaultConfig.maxRedirects),
    maxBodyBytes: clamp(merged.maxBodyBytes, 1_024, 262_144, defaultConfig.maxBodyBytes),
    maxConcurrency: clamp(merged.maxConcurrency, 1, 8, defaultConfig.maxConcurrency),
    rateLimitWindowMs: clamp(merged.rateLimitWindowMs, 1_000, 600_000, defaultConfig.rateLimitWindowMs),
    rateLimitMax: clamp(merged.rateLimitMax, 1, 60, defaultConfig.rateLimitMax),
    userAgent: userAgent || defaultConfig.userAgent,
    trustProxy: merged.trustProxy,
    serveStatic: merged.serveStatic,
    enableHsts: merged.enableHsts,
    siteUrl: normalizeSiteUrl(merged.siteUrl),
    searchLogPath: normalizeSearchLogPath(merged.searchLogPath),
  };
}

function normalizeSearchLogPath(value: string | null | undefined): string | null {
  if (!value?.trim()) return null;
  const resolved = path.resolve(value);
  const blocked = [path.resolve(process.cwd(), 'client', 'dist'), path.resolve(process.cwd(), 'client', 'public')];
  if (blocked.some((dir) => resolved === dir || resolved.startsWith(`${dir}${path.sep}`))) return null;
  return resolved;
}

function normalizeSiteUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    if ((url.protocol !== 'http:' && url.protocol !== 'https:') || url.username || url.password) return null;
    return url.origin;
  } catch {
    return null;
  }
}

function readInteger(name: string, fallback: number, min: number, max: number): number {
  const raw = process.env[name];
  if (raw === undefined || raw.trim() === '') return fallback;
  const value = Number(raw);
  if (!Number.isInteger(value) || value < min || value > max) {
    throw new Error(`Environment variable ${name} is invalid.`);
  }
  return value;
}

function readBoolean(name: string, fallback: boolean): boolean {
  const raw = process.env[name];
  if (raw === undefined || raw.trim() === '') return fallback;
  if (raw === 'true' || raw === '1') return true;
  if (raw === 'false' || raw === '0') return false;
  throw new Error(`Environment variable ${name} is invalid.`);
}

export function loadConfigFromEnv(): AppConfig {
  const origin = process.env.CLIENT_ORIGIN?.trim() || defaultConfig.clientOrigin;
  let clientOrigin = origin;
  try {
    const parsed = new URL(origin);
    if (parsed.username || parsed.password) {
      throw new Error('Environment variable CLIENT_ORIGIN is invalid.');
    }
    clientOrigin = parsed.origin;
  } catch (error) {
    if (error instanceof Error && error.message.includes('CLIENT_ORIGIN')) throw error;
    throw new Error('Environment variable CLIENT_ORIGIN is invalid.');
  }

  const userAgent = process.env.SCAN_USER_AGENT?.trim() || defaultConfig.userAgent;
  if (userAgent.length > 180 || /[\r\n]/.test(userAgent)) {
    throw new Error('Environment variable SCAN_USER_AGENT is invalid.');
  }

  const siteUrl = readSiteUrl();
  const searchLogPath = readSearchLogPath();
  return normalizeConfig({
    port: readInteger('PORT', defaultConfig.port, 1, 65_535),
    host: process.env.HOST?.trim() || defaultConfig.host,
    clientOrigin,
    timeoutMs: readInteger('SCAN_TIMEOUT_MS', defaultConfig.timeoutMs, 1_000, 30_000),
    maxRedirects: readInteger('SCAN_MAX_REDIRECTS', defaultConfig.maxRedirects, 0, 3),
    maxBodyBytes: readInteger('SCAN_MAX_BODY_BYTES', defaultConfig.maxBodyBytes, 1_024, 262_144),
    maxConcurrency: readInteger('SCAN_MAX_CONCURRENCY', defaultConfig.maxConcurrency, 1, 8),
    rateLimitWindowMs: readInteger('RATE_LIMIT_WINDOW_MS', defaultConfig.rateLimitWindowMs, 1_000, 600_000),
    rateLimitMax: readInteger('RATE_LIMIT_MAX', defaultConfig.rateLimitMax, 1, 60),
    userAgent,
    trustProxy: readBoolean('TRUST_PROXY', false),
    serveStatic: process.env.NODE_ENV === 'production',
    enableHsts: readBoolean('ENABLE_HSTS', false),
    ...(siteUrl ? { siteUrl } : {}),
    ...(searchLogPath !== undefined ? { searchLogPath } : {}),
  });
}

function readSearchLogPath(): string | null | undefined {
  if (process.env.SEARCH_LOG_PATH === undefined) return undefined;
  const raw = process.env.SEARCH_LOG_PATH.trim();
  if (!raw || raw.toLowerCase() === 'off') return null;
  const resolved = path.resolve(raw);
  const blocked = [path.resolve('client', 'dist'), path.resolve('client', 'public')];
  if (blocked.some((dir) => resolved === dir || resolved.startsWith(`${dir}${path.sep}`))) {
    throw new Error('Environment variable SEARCH_LOG_PATH is invalid.');
  }
  return resolved;
}

function readSiteUrl(): string | null {
  const raw = process.env.SITE_URL?.trim();
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    throw new Error('Environment variable SITE_URL is invalid.');
  }
  if ((url.protocol !== 'https:' && url.protocol !== 'http:') || url.username || url.password) {
    throw new Error('Environment variable SITE_URL is invalid.');
  }
  return url.origin;
}
